/**
 * useSectionCollapse
 *
 * Manages collapse/expand state for named UI sections (e.g. groups inside
 * Device State or Device Settings). State is global (not per-device) and
 * persisted to localStorage so it survives logout/re-login on the same browser.
 *
 * Usage:
 *   const { isSectionCollapsed, toggleSection } = useSectionCollapse();
 *   isSectionCollapsed("Connectivity")  // true = collapsed
 *   toggleSection("Connectivity")
 *
 * To reset to defaults from anywhere (e.g. Reset Layout):
 *   import { resetSectionCollapse } from './hooks/useSectionCollapse';
 *   resetSectionCollapse();
 */

import { useState, useCallback, useEffect } from "react";

const STORAGE_KEY = "rw_section_collapse";

// Sections collapsed on first load and after Reset Layout.
// User toggles override these and are remembered per-browser.
export const DEFAULT_SECTION_COLLAPSE = {
  // Device State
  "BLE": true,
  "Capacity": true,
  "No Tour": true,
  "Connectivity": true,
  "Firmware & Power": true,
  "Device Info": true,
  // Device Settings
  "BLE Credential Settings": true,
  "Beeper Settings": true,
  "Reader Settings": true,
  "Connectivity Settings": true,
  "Wi-Fi Settings": true,
  "Time Zone Settings": true,
  "Autolock Settings": true,
};

const save = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
};

const load = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) {
      // First visit — persist and return defaults
      save(DEFAULT_SECTION_COLLAPSE);
      return DEFAULT_SECTION_COLLAPSE;
    }
    return JSON.parse(stored);
  } catch {
    return DEFAULT_SECTION_COLLAPSE;
  }
};

/**
 * Resets all section collapse states to DEFAULT_SECTION_COLLAPSE and
 * notifies all mounted useSectionCollapse hooks to re-render.
 * Call this from handleResetLayout in AppContent.
 */
export const resetSectionCollapse = () => {
  save(DEFAULT_SECTION_COLLAPSE);
  try {
    window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY }));
  } catch {}
};

export function useSectionCollapse() {
  const [collapsedSections, setCollapsedSections] = useState(load);

  // Re-sync when resetSectionCollapse() fires a storage event
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        setCollapsedSections(load());
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const isSectionCollapsed = useCallback(
    (sectionId) => !!collapsedSections[sectionId],
    [collapsedSections]
  );

  const toggleSection = useCallback((sectionId) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      save(next);
      return next;
    });
  }, []);

  return { isSectionCollapsed, toggleSection };
}
